import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config";
import { useAuth } from "../context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { useSuccessFailure } from "../context/SuccessFailureContext"
import { Helmet } from 'react-helmet-async';

const EditProfile = () => {
  const eventTitle = process.env.REACT_APP_EVENT_TITLE || 'EventSawa';
  const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || 'We make events sawa';
  const { user, isLoggedIn } = useAuth();
  // console.log("User object from useAuth:", user);
  const userId = user?.id;
  // const { slug } = useParams();

  const [profile, setProfile] = useState({
    username: user?.username || "", // ✅ Ensure username is prefilled
    bio: user?.bio || "",
    website: user?.website || "",
    profile_picture: user?.profile_picture || "",
  });


  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false); // For loading indicator on submit
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false); // Show success modal
  const navigate = useNavigate();  // ✅ Initialize navigation
  const { showSuccessModal, showFailureModal } = useSuccessFailure()

  useEffect(() => {
    // Wait for authentication context to load
    if (isLoggedIn === undefined) {
      return;
    }
    
    // Once auth is loaded, set auth loading to false
    setAuthLoading(false);
    
    if (!user?.username) {
      setLoading(false);
      return;
    }
    
    // console.log("Username:", user.username);
    const accessToken = localStorage.getItem("access_token");

    axios
      .get(`${config.API_BASE_URL}/profiles/profile/username/${user?.username}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((res) => {
        const data = res.data;
        setProfile({
          username: data.username || "",
          bio: data.bio || "",
          website: data.website || "",
          profile_picture: data.profile_picture || "",
        });
        setLoading(false);
      })
      .catch((err) => {
        // console.error(err);
        setError("Failed to load profile.");
        setLoading(false);
      });
  }, [user, isLoggedIn]);


  // **Frontend Validation**
  const validateFields = () => {
    // if (!profile.username || profile.username.trim() === "") return "Username is required.";
    if (profile.bio && profile.bio.length > 300) return "Bio must be 300 characters or less.";
    if (profile.website && !isValidWebsite(profile.website)) return "Enter a valid website URL.";
    return null; // No errors
  };

  // Helper function to validate website URLs
  const isValidWebsite = (url) => {
    // Remove whitespace and convert to lowercase for validation
    const cleanUrl = url.trim().toLowerCase();
    
    // Debug: Log what we're validating
    console.log('Validating URL:', cleanUrl);
    
    // Empty string is valid (optional field)
    if (!cleanUrl) return true;
    
    // Simple validation: check for domain with TLD
    // This will match: www.example.com, example.com, https://example.com
    const domainRegex = /^(https?:\/\/)?(www\.)?[a-z0-9.-]+\.[a-z]{2,}$/i;
    
    const isValid = domainRegex.test(cleanUrl);
    console.log('Domain regex test:', isValid, 'Pattern:', domainRegex);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for bio field with character limit
    if (name === 'bio') {
      if (value.length <= 300) {
        setProfile({ ...profile, [name]: value });
      }
      // If over limit, don't update the state (prevents typing)
    } else {
      setProfile({ ...profile, [name]: value });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop();
    const shortFilename = `profile_pic_${Date.now()}.${fileExtension}`;
    const renamedFile = new File([file], shortFilename, { type: file.type });

    setImage(renamedFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateFields();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError("");

    let formData = new FormData();

    // ✅ Ensure username is always included
    // formData.append("username", profile.username.trim() || user?.username);

    if (profile.bio && profile.bio !== user?.bio) {
      formData.append("bio", profile.bio);
    }
    if (profile.website && profile.website !== user?.website) {
      formData.append("website", profile.website);
    }
    if (image) {
      formData.append("profile_picture", image);
    }

    // console.log("Sending Data:", Object.fromEntries(formData.entries())); // Debugging

    // ✅ Log the form data being sent (including username)
    // console.log("Slug:", formData.get("username"));

    axios
      .patch(`${config.API_BASE_URL}/profiles/edit-profile/${user?.username}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      })
      .then((res) => {
        setProfile(res.data);
        // setSuccess(true); // Show success modal

        showSuccessModal("Profile updated successfully!") // ✅ Show success

        // ✅ Redirect to the profile page after 2 seconds
        setTimeout(() => {
          navigate(`/profile/${res.data.slug}`);

        }, 2000);
      })
      .catch((err) => {
        // console.error("Axios Error:", err);
        const message = err.response?.data?.message || "Error updating profile"
        setError(message)
        showFailureModal(message)
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-lg p-8 sm:p-12">
      <Helmet>
        <title>{eventTitle}</title> {/* Set page title from environment variable */}
        <meta name="description" content={eventDescription} />
      </Helmet>
      
      {/* Show loading spinner while waiting for authentication */}
      {authLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
        </div>
      ) : (
        <>
          {user?.first_name && user?.last_name && (
            <h2 className="text-xl font-medium text-center text-gray-700">
              {user.first_name} {user.last_name}
            </h2>
          )}

          {user?.username && <p className="text-center text-gray-500 mb-5">@{user.username}</p>}

          {error && <p className="text-center text-red-500">{error}</p>}

      <div className="flex flex-col items-center">
        <label className="relative cursor-pointer">
          {image || profile?.profile_picture ? (
            <img
              src={image ? URL.createObjectURL(image) : profile?.profile_picture}
              alt="Profile"
              className="w-24 h-24 rounded-full object-cover border-2 border-gray-300 shadow-md"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gray-200 border-2 border-gray-300 shadow-md flex items-center justify-center">
              <svg 
                className="w-12 h-12 text-gray-400" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" 
                  clipRule="evenodd" 
                />
              </svg>
            </div>
          )}
          <input type="file" onChange={handleImageChange} className="hidden" />
        </label>
        <p className="text-sm text-gray-500 mt-2">Click to change profile picture</p>
      </div>

      <form onSubmit={handleSubmit} className="mt-6">
        <div className="grid gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600">Bio</label>
            <textarea
              name="bio"
              value={profile.bio}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-lg"
              rows="4"
              placeholder="Tell us about yourself..."
            ></textarea>
            <div className="flex justify-between items-center mt-1">
              <span className={`text-xs ${profile.bio.length >= 300 ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                {profile.bio.length}/300 characters
              </span>
              {profile.bio.length >= 300 && (
                <span className="text-xs text-red-500">Character limit reached</span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600">Website</label>
            <input
              type="url"
              name="website"
              value={profile.website}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-lg"
              placeholder="www.example.com or https://example.com"
            />
          </div>
        </div>

        <button
          type="submit"
          className="w-full mt-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex justify-center items-center"
          disabled={submitting}
        >
          {submitting ? <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div> : "Save Changes"}
        </button>
      </form>

      {/* Success Modal */}
      {success && (
        <div id="successModal" className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative p-4 w-full max-w-md bg-white rounded-lg shadow">
            <button
              type="button"
              className="absolute top-2.5 right-2.5 text-gray-400 hover:bg-gray-200 hover:text-gray-900 rounded-lg p-1.5"
              onClick={() => setSuccess(false)}
            >
              ✖
            </button>
            <div className="w-12 h-12 bg-green-100 p-2 flex items-center justify-center mx-auto mb-3.5 rounded-full">
              <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
            </div>
            <p className="mb-4 text-lg font-semibold text-gray-900">Profile Updated Successfully</p>
            <button onClick={() => setSuccess(false)} className="py-2 px-3 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg">
              Continue
            </button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default EditProfile;
